import React, { useState, useContext, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import {
    AppBar,
    Toolbar,
    IconButton,
    Typography,
    Box,
    Drawer,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Divider,
    useTheme,
    useMediaQuery,
    Avatar,
    Menu,
    MenuItem,
    Badge,
    Tooltip,
    Chip,
    Stack,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import * as Lucide from 'lucide-react';
import LocationOnIcon from '@mui/icons-material/LocationOn';
// Ícones extras oficiais do Lucide (mesmo traço/estilo). O Lucide padrão não tem
// vaca nem celeiro — necessários para o menu do plano LEITE.
import { cowHead, cowUdderDroplets, bullHead, barn, farm } from '@lucide/lab';
import { ThemeContext } from '../../theme/AppThemeProvider';
import { useKeycloak } from '@react-keycloak/web';
import AuthContext from '../../context/AuthContext';
import { useBootstrap } from '../../context/BootstrapContext';
import { usePermissao } from '../../security/usePermissao';
import { usePerfilPlano } from '../../security/usePerfilPlano';
import { AZUL_CLARO } from '../../theme/cores';
import { barraTemFundoEscuro, logoParaFundo } from '../../utils/marca';
import useResumoPlanosAcao from '../../hooks/useResumoPlanosAcao';
import { usePWAInstall } from '../../context/PWAInstallContext';

/** Rota do plano de ações: o item do menu mostra quantas ações estão em aberto. */
const ROTA_ACOES = '/acoes';
const DRAWER_WIDTH = 250;
// Recolhida: cabe o icone e o alvo de toque, sem rotulo. 72px e o valor do
// Material para "navigation rail".
const DRAWER_WIDTH_RECOLHIDO = 72;
const HEADER_HEIGHT = 64;
// Barra clara (perfil do plano `menuClaro`): o cabeçalho cresce para a logo maior do
// cliente, e o item selecionado usa um azul mais claro que o primário.
const HEADER_HEIGHT_CLARO = 88;
const AZUL_SELECIONADO = AZUL_CLARO;
// Tela baixa (notebook com escala de 125–150%, janela reduzida): itens e cabeçalho mais
// compactos, para o menu inteiro caber sem rolar — o "tile height" reduzido que o
// Material usa no desktop. Abaixo disso, só a lista rola; cabeçalho e rodapé ficam fixos.
const TELA_BAIXA = '@media (max-height: 820px)';
const TELA_MUITO_BAIXA = '@media (max-height: 700px)';
const HEADER_HEIGHT_CLARO_BAIXA = 72;

/**
 * Assinatura sob a logo (perfil do plano, ex.: "MICROBIOLOGIA DO LEITE"). Justificada na
 * largura da coluna da logo, em maiúsculas espaçadas, para ler como parte da marca e não
 * como um rótulo solto.
 */
function AssinaturaMarca({ texto, claro }) {
    if (!texto) return null;
    return (
        <Typography
            component="span"
            sx={{
                display: 'block',
                mt: 0.6,
                fontSize: '0.6rem',
                fontWeight: 700,
                letterSpacing: '0.16em',
                lineHeight: 1,
                textTransform: 'uppercase',
                whiteSpace: 'nowrap',
                textAlign: 'justify',
                textAlignLast: 'justify',
                color: (t) => (!claro ? 'rgba(255,255,255,0.75)' : t.palette.mode === 'dark' ? t.palette.text.secondary : '#1E3A5F'),
            }}
        >
            {texto}
        </Typography>
    );
}

// A escolha de recolher e uma preferencia de trabalho, nao um estado de tela:
// quem recolheu quer a barra recolhida amanha tambem.
const CHAVE_SIDEBAR = 'bioailab_crm_sidebar_recolhida';

// localStorage LANCA em aba anonima ou com armazenamento bloqueado — sem o
// try/catch o menu inteiro (e portanto o app) quebraria.
const lerSidebarRecolhida = () => {
    try {
        return localStorage.getItem(CHAVE_SIDEBAR) === '1';
    } catch {
        return false;
    }
};

const salvarSidebarRecolhida = (recolhida) => {
    try {
        localStorage.setItem(CHAVE_SIDEBAR, recolhida ? '1' : '0');
    } catch {
        /* segue sem memoria */
    }
};

// Papéis de negócio que vêm no token (Keycloak), com rótulo e cor, pra exibir
// ao lado do nome do usuário. Roles internas do Keycloak (offline_access,
// uma_authorization, default-roles-*) ficam de fora por não estarem aqui.
const ROLE_META = {
    ADMIN: { label: 'Admin', color: 'primary' },
    GESTOR: { label: 'Gestor', color: 'info' },
    LAB: { label: 'Laboratório', color: 'success' },
    COLETOR: { label: 'Coletor', color: 'warning' },
    PARCEIRO: { label: 'Parceiro', color: 'secondary' },
    GUEST: { label: 'Convidado', color: 'default' },
};

// Catálogo dinâmico: chave = valor gravado no campo `icon` de cada item de
// menu (editado no CRM admin, iot-platform-front/src/components/MenuItemModal.jsx
// — ICON_CATALOG lá precisa ter a MESMA chave, senão o ícone escolhido no
// admin não aparece aqui). Ícones em lucide-react, com o mesmo `size` usado
// na navegação do admin (Nav.jsx) pra manter a proporção visual consistente.
const iconMap = {
    // legado — chaves já gravadas em itens de menu existentes
    Home: <Lucide.Home size={20} />,
    Settings: <Lucide.Settings size={20} />,
    Devices: <Lucide.Cpu size={20} />,
    Science: <Lucide.FlaskConical size={20} />,
    Analytics: <Lucide.BarChart3 size={20} />,
    Assessment: <Lucide.ClipboardList size={20} />,
    ListAlt: <Lucide.List size={20} />,
    Sensors: <Lucide.Activity size={20} />,
    Dashboard: <Lucide.LayoutDashboard size={20} />,
    Menu: <Lucide.Menu size={20} />,
    Build: <Lucide.Wrench size={20} />,
    Storage: <Lucide.Database size={20} />,
    People: <Lucide.Users size={20} />,
    Security: <Lucide.ShieldCheck size={20} />,
    Folder: <Lucide.Folder size={20} />,
    AdUnits: <Lucide.Router size={20} />,
    Description: <Lucide.FileText size={20} />,
    Wifi: <Lucide.Wifi size={20} />,
    Insights: <Lucide.LineChart size={20} />,
    Support: <Lucide.LifeBuoy size={20} />,
    SupportAgent: <Lucide.LifeBuoy size={20} />,

    // catálogo ampliado
    Building2: <Lucide.Building2 size={20} />,
    KeyRound: <Lucide.KeyRound size={20} />,
    ScrollText: <Lucide.ScrollText size={20} />,
    HardDrive: <Lucide.HardDrive size={20} />,
    Bell: <Lucide.Bell size={20} />,
    Calendar: <Lucide.Calendar size={20} />,
    Mail: <Lucide.Mail size={20} />,
    Globe: <Lucide.Globe size={20} />,
    Zap: <Lucide.Zap size={20} />,
    AlertTriangle: <Lucide.AlertTriangle size={20} />,
    Info: <Lucide.Info size={20} />,
    Radio: <Lucide.Radio size={20} />,
    Atom: <Lucide.Atom size={20} />,
    SlidersHorizontal: <Lucide.SlidersHorizontal size={20} />,
    BookOpen: <Lucide.BookOpen size={20} />,
    Microscope: <Lucide.Microscope size={20} />,
    TestTube: <Lucide.TestTube size={20} />,
    Beaker: <Lucide.Beaker size={20} />,
    Droplet: <Lucide.Droplet size={20} />,
    Droplets: <Lucide.Droplets size={20} />,
    Waves: <Lucide.Waves size={20} />,
    Leaf: <Lucide.Leaf size={20} />,
    Gauge: <Lucide.Gauge size={20} />,
    Thermometer: <Lucide.Thermometer size={20} />,
    TrendingUp: <Lucide.TrendingUp size={20} />,
    Lock: <Lucide.Lock size={20} />,
    HelpCircle: <Lucide.HelpCircle size={20} />,
    Server: <Lucide.Server size={20} />,
    Check: <Lucide.Check size={20} />,

    // plano LEITE — mesmas chaves do ICON_CATALOG do IOT (MenuItemModal.jsx).
    // Os do @lucide/lab renderizam pelo `Lucide.Icon` com o desenho em `iconNode`.
    Milk: <Lucide.Milk size={20} />,
    CowHead: <Lucide.Icon iconNode={cowHead} size={20} />,
    CowUdderDroplets: <Lucide.Icon iconNode={cowUdderDroplets} size={20} />,
    BullHead: <Lucide.Icon iconNode={bullHead} size={20} />,
    Barn: <Lucide.Icon iconNode={barn} size={20} />,
    Farm: <Lucide.Icon iconNode={farm} size={20} />,
    MapPin: <Lucide.MapPin size={20} />,
    // Alfinete preenchido de mapa (Material): mesma chave do ICON_CATALOG do IOT.
    MapPinFilled: <LocationOnIcon sx={{ fontSize: 22 }} />,
};

// Exportado: o redirecionamento inicial (InnerRoutes) usa a mesma regra para achar o
// "primeiro item do menu" quando a conta não tem Home.
export const HIDDEN_MENU_ROUTES = ['/configuracoes', '/graficos', '/suporte'];

const getAvatarData = (username) => {
    const key = `avatar-${username}`;
    try {
        const stored = JSON.parse(localStorage.getItem(key));
        if (stored?.initials) return stored;
    } catch {}

    const initials =
        username
            ?.split(' ')
            .slice(0, 2)
            .map((p) => p.charAt(0).toUpperCase())
            .join('') || '??';
    const palette = ['#FFB4AB', '#FFB74D', '#90CAF9', '#81C784', '#D7E2F2'];
    const idx = (username.charCodeAt(0) + username.length) % palette.length;
    const data = { initials, color: palette[idx], textColor: '#0E223B' };
    localStorage.setItem(key, JSON.stringify(data));
    return data;
};

export default function MenuComponent() {
    const theme = useTheme();
    const isMobileOrTablet = useMediaQuery(theme.breakpoints.down('md'));
    const navigate = useNavigate();
    const { tenant } = useParams();
    const { pathname } = useLocation();
    const { logout } = useContext(AuthContext);
    const { preference: themePreference, setColorMode } = useContext(ThemeContext);
    const { keycloak } = useKeycloak();
    const { bootstrap } = useBootstrap();

    const [mobileOpen, setMobileOpen] = useState(false);
    // Aberta por padrao (lerSidebarRecolhida devolve false quando nunca foi
    // escolhido). So vale no desktop — no mobile o Drawer e temporary.
    const [recolhida, setRecolhida] = useState(lerSidebarRecolhida);
    const [userAnchor, setUserAnchor] = useState(null);
    const [themeAnchor, setThemeAnchor] = useState(null);
    const [logoError, setLogoError] = useState(false);

    const username = keycloak.tokenParsed?.preferred_username || 'Usuário';
    const { papeis, plano } = usePermissao();
    const { menuClaro, nome: nomePlano, assinatura } = usePerfilPlano();
    const temAcoes = (bootstrap?.menu?.itensMenus || []).some((i) => i.rotas === ROTA_ACOES);
    const resumoAcoes = useResumoPlanosAcao(temAcoes);
    const contadorDo = (path) => (path === ROTA_ACOES ? resumoAcoes?.abertas || 0 : 0);
    const { podeInstalar, abrirModal: abrirModalInstalacao } = usePWAInstall();
    const roleChips = useMemo(
        () => papeis.filter((r) => ROLE_META[r]).map((r) => ({ key: r, ...ROLE_META[r] })),
        [papeis]
    );
    const [avatar, setAvatar] = useState(() => getAvatarData(username));

    useEffect(() => {
        setAvatar(getAvatarData(username));
    }, [username]);

    // Logo do cliente pelo fundo do cabeçalho (claro só no menu claro + tema claro).
    const logoCliente = logoParaFundo(
        bootstrap?.client,
        barraTemFundoEscuro({ menuClaro, modoEscuro: theme.palette.mode === 'dark' })
    );

    useEffect(() => {
        setLogoError(false);
    }, [logoCliente]);

    const { specialMenuItems, menuItems } = useMemo(() => {
        const rawMenus = bootstrap?.menu?.itensMenus || [];
        return {
            specialMenuItems: {
                settings: rawMenus.find((i) => i.rotas === '/configuracoes'),
                support: rawMenus.find((i) => i.rotas === '/suporte'),
            },
            menuItems: rawMenus
                .filter((i) => !HIDDEN_MENU_ROUTES.includes(i.rotas))
                .map((i) => ({
                    label: i.nome,
                    path: i.rotas,
                    icon: iconMap[i.icon] || <Lucide.Settings />,
                })),
        };
    }, [bootstrap]);

    const toTenant = useCallback((path = '') => {
        const cleanPath = path.replace(/^\//, '');
        return cleanPath ? `/${tenant}/${cleanPath}` : `/${tenant}`;
    }, [tenant]);

    const isActive = useCallback((path) => {
        const targetPath = toTenant(path);
        if (targetPath === `/${tenant}`) {
            return pathname === targetPath || pathname === `${targetPath}/`;
        }
        return pathname === targetPath || pathname.startsWith(targetPath + '/');
    }, [pathname, tenant, toTenant]);

    const currentPageLabel = useMemo(() => {
        const allItems = [
            ...menuItems,
            { label: 'Configurações', path: '/configuracoes' },
            { label: 'Suporte', path: '/suporte' },
            { label: 'Gráficos', path: '/graficos' },
        ];
        return allItems.find((item) => isActive(item.path))?.label || '';
    }, [menuItems, isActive]);

    const handleDrawerToggle = () => setMobileOpen(!mobileOpen);

    const alternarRecolhida = useCallback(() => {
        setRecolhida((atual) => {
            salvarSidebarRecolhida(!atual);
            return !atual;
        });
    }, []);

    // Largura efetiva da barra fixa. O AppBar e o espacador `aside` seguem este
    // mesmo valor — se qualquer um dos tres divergir, o conteudo desalinha ou
    // aparece uma faixa vazia.
    const larguraBarra = recolhida ? DRAWER_WIDTH_RECOLHIDO : DRAWER_WIDTH;

    // Uma transicao so, reutilizada nos tres lugares, para tudo deslizar junto.
    const transicaoBarra = theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
    });

    const handleNavigate = (path) => {
        navigate(toTenant(path));
        if (isMobileOrTablet) setMobileOpen(false);
    };

    const handleLogout = () => {
        logout();
    };

    const getNavItemStyle = useCallback((active = false, isError = false) => ({
        borderRadius: 1.5,
        mb: 0.5,
        py: 1,
        [TELA_BAIXA]: { py: 0.6, mb: 0.25 },
        transition: 'all 0.2s',
        ...(isError
            ? {
                  color: 'error.main',
                  '&:hover': { bgcolor: alpha(theme.palette.error.main, 0.06) },
              }
            : active && menuClaro
            ? {
                  bgcolor: alpha(AZUL_SELECIONADO, theme.palette.mode === 'dark' ? 0.18 : 0.1),
                  color: AZUL_SELECIONADO,
                  '& .MuiListItemIcon-root': { color: AZUL_SELECIONADO },
                  '& .MuiListItemText-primary': { fontWeight: 700 },
                  '&:hover': { bgcolor: alpha(AZUL_SELECIONADO, theme.palette.mode === 'dark' ? 0.24 : 0.14) },
              }
            : active
            ? {
                  bgcolor: alpha(theme.palette.primary.main, 0.08),
                  color: 'primary.main',
                  '& .MuiListItemIcon-root': { color: 'primary.main' },
                  '& .MuiListItemText-primary': { fontWeight: 700 },
              }
            : menuClaro
            ? {
                  color: 'text.secondary',
                  '&:hover': {
                      bgcolor: alpha(AZUL_SELECIONADO, 0.06),
                      color: AZUL_SELECIONADO,
                      '& .MuiListItemIcon-root': { color: AZUL_SELECIONADO },
                  },
              }
            : {
                  color: 'text.secondary',
                  '&:hover': {
                      bgcolor: alpha(theme.palette.primary.main, 0.04),
                      color: 'primary.main',
                      '& .MuiListItemIcon-root': { color: 'primary.main' },
                  },
              }),
    }), [theme, menuClaro]);

    // Funcao, nao constante: o Drawer temporary do mobile SEMPRE renderiza
    // completo (recolher e so do desktop), entao os dois precisam de versoes
    // diferentes do mesmo conteudo.
    const montarConteudo = (compacto = false) => (
        <Box
            sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                bgcolor: 'background.paper',
                overflowX: 'hidden',
                // Quem rola é só a lista de navegação (abaixo), não o painel inteiro.
                overflowY: 'hidden',
            }}
        >
            {/* Header: logo + controle de recolher */}
            <Box
                component="header"
                className="cabecalho-barra"
                role="button"
                aria-label="Ir para a página inicial"
                onClick={() => handleNavigate('')}
                sx={{
                    p: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: compacto ? 'center' : 'flex-start',
                    gap: compacto ? 0 : 1.5,
                    height: menuClaro ? HEADER_HEIGHT_CLARO + (assinatura && !compacto ? 8 : 0) : HEADER_HEIGHT,
                    ...(menuClaro && {
                        [TELA_BAIXA]: { height: HEADER_HEIGHT_CLARO_BAIXA + (assinatura && !compacto ? 8 : 0), py: 1.5 },
                    }),
                    overflow: 'hidden',
                    // Ancora do botao sobreposto quando a barra esta recolhida.
                    position: 'relative',
                    // Barra clara: o cabecalho tem o mesmo fundo do resto da barra.
                    bgcolor: menuClaro
                        ? 'background.paper'
                        : theme.palette.mode === 'dark' ? '#060A10' : 'primary.main',
                    color: menuClaro ? 'text.secondary' : 'white',
                    boxSizing: 'border-box',
                    cursor: 'pointer',
                    flexShrink: 0,
                    transition: 'background-color 0.2s',
                    '&:hover': {
                        bgcolor: menuClaro
                            ? 'action.hover'
                            : theme.palette.mode === 'dark' ? '#0D1117' : 'primary.dark',
                    },
                    // Recolhida: o logo cede lugar ao botao de expandir no hover.
                    ...(compacto && {
                        '&:hover .logo-barra': { opacity: 0 },
                        '&:hover .alternar-barra': {
                            opacity: 1,
                            pointerEvents: 'auto',
                        },
                    }),
                }}
            >
                {/* Recolhida, sempre o icone quadrado da AUFTEK: a logo do cliente e
                    horizontal e ficava espremida nos ~40px que sobram. */}
                {logoCliente && !logoError && !compacto ? (
                    // Coluna da largura da logo: a assinatura justificada ocupa exatamente
                    // a mesma largura, como parte da marca.
                    <Box className="logo-barra" sx={{ display: 'inline-flex', flexDirection: 'column', flexShrink: 0, minWidth: 0, transition: 'opacity 0.15s' }}>
                        <Box
                            component="img"
                            src={logoCliente}
                            alt={bootstrap.client.name || 'Logo do cliente'}
                            sx={{
                                height: menuClaro ? (assinatura ? 46 : 52) : 30,
                                ...(menuClaro && { [TELA_BAIXA]: { height: assinatura ? 36 : 42 } }),
                                maxWidth: menuClaro ? 196 : 180,
                                objectFit: 'contain',
                                objectPosition: 'left center',
                            }}
                            onError={() => setLogoError(true)}
                        />
                        <AssinaturaMarca texto={assinatura} claro={menuClaro} />
                    </Box>
                ) : (
                    <>
                        <Box
                            component="img"
                            className="logo-barra"
                            src="/logo192.png"
                            alt="AUFTEK"
                            sx={{
                                width: compacto ? 36 : 28,
                                height: compacto ? 36 : 28,
                                borderRadius: '8px',
                                objectFit: 'contain',
                                transition: 'opacity 0.15s',
                            }}
                        />
                        {!compacto && (
                            <Box sx={{ display: 'inline-flex', flexDirection: 'column', minWidth: 0 }}>
                                <Typography
                                    variant="subtitle1"
                                    component="h1"
                                    sx={{
                                        fontWeight: 800,
                                        letterSpacing: '-0.5px',
                                        m: 0,
                                        whiteSpace: 'nowrap',
                                        lineHeight: assinatura ? 1.2 : undefined,
                                    }}
                                >
                                    BioAILab CRM
                                </Typography>
                                <AssinaturaMarca texto={assinatura} claro={menuClaro} />
                            </Box>
                        )}
                    </>
                )}

                {/* So no desktop: no mobile o Drawer e temporary e fecha pelo
                    proprio overlay, entao recolher nao faz sentido la. */}
                {!isMobileOrTablet && (
                    <Tooltip
                        title={compacto ? 'Expandir menu' : 'Recolher menu'}
                        placement="right"
                    >
                        <IconButton
                            className="alternar-barra"
                            size="small"
                            aria-label={
                                compacto
                                    ? 'Expandir menu de navegação'
                                    : 'Recolher menu de navegação'
                            }
                            aria-expanded={!compacto}
                            onClick={(e) => {
                                // O header inteiro navega para a Home; sem isto o
                                // clique no botao faria as duas coisas.
                                e.stopPropagation();
                                alternarRecolhida();
                            }}
                            sx={{
                                color: 'inherit',
                                flexShrink: 0,
                                ...(compacto
                                    ? {
                                          // Recolhida nao ha largura para ficar AO
                                          // LADO do logo — ele fica POR CIMA, e so
                                          // aparece no hover (regra no header).
                                          position: 'absolute',
                                          top: '50%',
                                          left: '50%',
                                          transform: 'translate(-50%, -50%)',
                                          opacity: 0,
                                          pointerEvents: 'none',
                                          transition: 'opacity 0.15s',
                                      }
                                    : { ml: 'auto' }),
                            }}
                        >
                            {compacto ? (
                                <Lucide.PanelLeftOpen size={20} aria-hidden="true" />
                            ) : (
                                <Lucide.PanelLeftClose size={20} aria-hidden="true" />
                            )}
                        </IconButton>
                    </Tooltip>
                )}
            </Box>

            <Divider aria-hidden="true" />

            {/* Section label — escondido na barra recolhida: "Navegação" em 72px
                quebraria em duas linhas tortas e nao acrescenta nada ao lado de
                icones que ja tem tooltip. */}
            <Typography
                variant="caption"
                sx={{
                    px: 2,
                    pt: 1.5,
                    pb: 0.25,
                    display: compacto || menuClaro ? 'none' : 'block',
                    color: 'text.disabled',
                    fontWeight: 700,
                    letterSpacing: 1,
                    fontSize: '0.6rem',
                    textTransform: 'uppercase',
                    flexShrink: 0,
                }}
            >
                Navegação
            </Typography>

            {/* Main navigation */}
            <List
                component="nav"
                aria-label="Menu principal"
                sx={{
                    flexGrow: 1,
                    minHeight: 0,
                    overflowY: 'auto',
                    px: 1.5,
                    py: 1,
                    [TELA_BAIXA]: { py: 0.5 },
                    // Barra de rolagem fina e discreta, só quando a lista não cabe.
                    scrollbarWidth: 'thin',
                    '&::-webkit-scrollbar': { width: 6 },
                    '&::-webkit-scrollbar-thumb': { borderRadius: 3, bgcolor: alpha(theme.palette.text.primary, 0.18) },
                }}
            >
                {menuItems.map((item) => {
                    const active = isActive(item.path);
                    return (
                        <Tooltip
                            key={item.label}
                            // Recolhida, o icone sozinho nao diz o destino — o
                            // tooltip e o que mantem o menu utilizavel.
                            title={compacto ? item.label : ''}
                            placement="right"
                        >
                            <ListItemButton
                                onClick={() => handleNavigate(item.path)}
                                aria-current={active ? 'page' : undefined}
                                aria-label={item.label}
                                sx={{
                                    ...getNavItemStyle(active),
                                    ...(compacto && {
                                        justifyContent: 'center',
                                        px: 1,
                                    }),
                                }}
                            >
                                <ListItemIcon
                                    sx={{
                                        minWidth: compacto ? 0 : 36,
                                        color: 'inherit',
                                        justifyContent: 'center',
                                    }}
                                    aria-hidden="true"
                                >
                                    {/* Recolhida, o contador vira um ponto no icone. */}
                                    {compacto && contadorDo(item.path) > 0 ? (
                                        <Badge variant="dot" color="primary" overlap="circular">
                                            {item.icon}
                                        </Badge>
                                    ) : (
                                        item.icon
                                    )}
                                </ListItemIcon>
                                {!compacto && (
                                    <ListItemText
                                        primary={item.label}
                                        primaryTypographyProps={{
                                            variant: 'body2',
                                            fontWeight: active ? 700 : 500,
                                        }}
                                    />
                                )}
                                {!compacto && contadorDo(item.path) > 0 && (
                                    <Box
                                        component="span"
                                        aria-label={`${contadorDo(item.path)} em aberto`}
                                        sx={{
                                            ml: 1, px: 0.9, minWidth: 22, height: 20, borderRadius: 1,
                                            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                            fontSize: '0.72rem', fontWeight: 700,
                                            bgcolor: active ? alpha(AZUL_SELECIONADO, 0.15) : 'action.selected',
                                            color: active ? AZUL_SELECIONADO : 'text.secondary',
                                        }}
                                    >
                                        {contadorDo(item.path)}
                                    </Box>
                                )}
                            </ListItemButton>
                        </Tooltip>
                    );
                })}
            </List>

            {/* Footer: theme toggle + support + logout */}
            <Box
                component="nav"
                aria-label="Menu de usuário e suporte"
                sx={{
                    px: 1.5,
                    pb: 2,
                    flexShrink: 0,
                    [TELA_BAIXA]: { pb: 1, '& > hr': { mb: 0.75 } },
                    // Recolhida: some o rotulo e centraliza o icone de TODOS os
                    // itens daqui de uma vez. Feito pelo container e nao item a
                    // item para nao ter que lembrar disso a cada botao novo.
                    ...(compacto && {
                        '& .MuiListItemText-root': { display: 'none' },
                        '& .MuiListItemIcon-root': {
                            minWidth: 0,
                            justifyContent: 'center',
                        },
                        '& .MuiListItemButton-root': {
                            justifyContent: 'center',
                            px: 1,
                        },
                        // A seta do submenu de tema nao cabe e nao faz falta.
                        '& .seta-submenu': { display: 'none' },
                    }),
                }}
            >
                <Divider sx={{ mb: 1.5 }} aria-hidden="true" />

                {/* Botão Tema */}
                <ListItemButton
                    id="theme-button"
                    aria-controls={Boolean(themeAnchor) ? 'theme-menu' : undefined}
                    aria-haspopup="true"
                    aria-expanded={Boolean(themeAnchor)}
                    onClick={(e) => setThemeAnchor(e.currentTarget)}
                    aria-label="Tema"
                    title={compacto ? 'Tema' : undefined}
                    sx={getNavItemStyle(false)}
                >
                    <ListItemIcon sx={{ minWidth: 36, color: 'inherit' }} aria-hidden="true">
                        {theme.palette.mode === 'dark'
                            ? <Lucide.Moon size={18} />
                            : <Lucide.Sun size={18} />
                        }
                    </ListItemIcon>
                    <ListItemText
                        primary="Tema"
                        primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                    />
                    <Lucide.ChevronRight
                        className="seta-submenu"
                        size={16}
                        color={theme.palette.text.disabled}
                        style={{ flexShrink: 0 }}
                    />
                </ListItemButton>

                {/* Submenu de tema */}
                <Menu
                    id="theme-menu"
                    anchorEl={themeAnchor}
                    open={Boolean(themeAnchor)}
                    onClose={() => setThemeAnchor(null)}
                    anchorOrigin={{ vertical: 'center', horizontal: 'right' }}
                    transformOrigin={{ vertical: 'center', horizontal: 'left' }}
                    PaperProps={{
                        elevation: 4,
                        sx: {
                            minWidth: 156,
                            borderRadius: 2,
                            ml: 0.5,
                            py: 0.5,
                        },
                    }}
                >
                    {[
                        { value: 'system', label: 'Sistema', icon: <Lucide.Monitor size={16} /> },
                        { value: 'light',  label: 'Claro',   icon: <Lucide.Sun size={16} /> },
                        { value: 'dark',   label: 'Escuro',  icon: <Lucide.Moon size={16} /> },
                    ].map(({ value, label, icon }) => (
                        <MenuItem
                            key={value}
                            onClick={() => { setColorMode(value); setThemeAnchor(null); }}
                            selected={themePreference === value}
                            sx={{
                                minHeight: 40,
                                borderRadius: 1.5,
                                mx: 0.5,
                                my: 0.25,
                                px: 1.5,
                                gap: 1.25,
                                '&.Mui-selected': {
                                    bgcolor: alpha(theme.palette.primary.main, 0.08),
                                    color: 'primary.main',
                                    '& .MuiListItemIcon-root': { color: 'primary.main' },
                                    '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.12) },
                                },
                            }}
                        >
                            <ListItemIcon sx={{ minWidth: 0, color: 'inherit' }} aria-hidden="true">
                                {icon}
                            </ListItemIcon>
                            <ListItemText
                                primary={label}
                                primaryTypographyProps={{
                                    variant: 'body2',
                                    fontWeight: themePreference === value ? 600 : 400,
                                }}
                            />
                            {themePreference === value && (
                                <Lucide.Check size={16} color={theme.palette.primary.main} style={{ flexShrink: 0 }} />
                            )}
                        </MenuItem>
                    ))}
                </Menu>

                <Divider sx={{ my: 1 }} aria-hidden="true" />

                {/* Caminho MANUAL de instalacao. Antes so existia o convite
                    automatico, de uma chance so: quem o fechasse sem querer, ou nao o
                    recebesse, ficava sem nenhuma forma de instalar pela interface.
                    Some sozinho quando o app ja esta instalado (display-mode
                    standalone) ou quando nao ha nada a oferecer. */}
                {podeInstalar && (
                    <ListItemButton
                        onClick={() => {
                            abrirModalInstalacao();
                            if (isMobileOrTablet) setMobileOpen(false);
                        }}
                        aria-label="Instalar aplicativo"
                        title={compacto ? 'Instalar aplicativo' : undefined}
                        sx={getNavItemStyle(false)}
                    >
                        <ListItemIcon sx={{ minWidth: 36, color: 'inherit' }} aria-hidden="true">
                            <Lucide.Download size={18} />
                        </ListItemIcon>
                        <ListItemText
                            primary="Instalar aplicativo"
                            primaryTypographyProps={{
                                variant: 'body2',
                                fontWeight: 500,
                            }}
                        />
                    </ListItemButton>
                )}

                {specialMenuItems.support && (
                    <ListItemButton
                        onClick={() => handleNavigate('/suporte')}
                        aria-current={isActive('/suporte') ? 'page' : undefined}
                        aria-label="Suporte"
                        title={compacto ? 'Suporte' : undefined}
                        sx={getNavItemStyle(isActive('/suporte'))}
                    >
                        <ListItemIcon sx={{ minWidth: 36, color: 'inherit' }} aria-hidden="true">
                            <Lucide.LifeBuoy size={18} />
                        </ListItemIcon>
                        <ListItemText
                            primary="Suporte"
                            primaryTypographyProps={{
                                variant: 'body2',
                                fontWeight: isActive('/suporte') ? 700 : 500,
                            }}
                        />
                    </ListItemButton>
                )}

                <ListItemButton
                    onClick={handleLogout}
                    aria-label="Sair"
                    title={compacto ? 'Sair' : undefined}
                    sx={getNavItemStyle(false, true)}
                >
                    <ListItemIcon sx={{ minWidth: 36, color: 'inherit' }} aria-hidden="true">
                        <Lucide.LogOut size={18} color={theme.palette.error.main} />
                    </ListItemIcon>
                    <ListItemText
                        primary="Sair"
                        primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                    />
                </ListItemButton>
            </Box>

            {/* Branding fixo */}
            <Box
                sx={{
                    px: 2,
                    py: 1.25,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 0.75,
                    flexShrink: 0,
                    borderTop: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                    // Tela muito baixa: a assinatura do rodapé cede o espaço aos itens.
                    [TELA_MUITO_BAIXA]: { display: 'none' },
                }}
            >
                <Box
                    component="img"
                    src="/logo192.png"
                    alt=""
                    aria-hidden="true"
                    sx={{ width: 13, height: 13, objectFit: 'contain', opacity: 0.45 }}
                />
                <Typography
                    variant="caption"
                    sx={{
                        fontSize: '0.62rem',
                        fontWeight: 700,
                        color: 'text.disabled',
                        letterSpacing: '0.02em',
                    }}
                >
                    BioAILab CRM
                </Typography>
            </Box>
        </Box>
    );

    return (
        <>
            <AppBar
                component="header"
                position="fixed"
                elevation={0}
                sx={{
                    width: { md: `calc(100% - ${larguraBarra}px)` },
                    ml: { md: `${larguraBarra}px` },
                    transition: { md: transicaoBarra },
                    bgcolor: theme.palette.mode === 'dark' ? '#060A10' : 'primary.main',
                    color: 'white',
                    height: HEADER_HEIGHT,
                    borderBottom: 'none',
                    boxShadow: theme.palette.mode === 'dark' ? 'none' : '0px 2px 8px rgba(0,0,0,0.12)',
                    maxWidth: '100vw',
                    overflow: 'hidden',
                }}
            >
                <Toolbar
                    sx={{
                        justifyContent: 'space-between',
                        height: HEADER_HEIGHT,
                        minHeight: `${HEADER_HEIGHT}px !important`,
                        overflow: 'hidden',
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: 0, flex: 1 }}>
                        {isMobileOrTablet && (
                            <IconButton
                                color="inherit"
                                aria-label="Abrir menu de navegação"
                                edge="start"
                                onClick={handleDrawerToggle}
                                sx={{ mr: 1 }}
                            >
                                <Lucide.Menu aria-hidden="true" />
                            </IconButton>
                        )}
                        <Box sx={{ minWidth: 0 }}>
                            {currentPageLabel && (
                                <Typography
                                    component="div"
                                    sx={{
                                        fontSize: { xs: '1.15rem', sm: '1.35rem' },
                                        fontWeight: 700,
                                        letterSpacing: '-0.3px',
                                        lineHeight: 1.2,
                                        color: 'white',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    {currentPageLabel}
                                </Typography>
                            )}
                        </Box>
                    </Box>

                    <Stack direction="row" spacing={1.5} alignItems="center">
                        <Tooltip title="Detalhes do Plano">
                            <Chip
                                icon={
                                    <Lucide.Crown
                                        aria-hidden="true"
                                        size={16}
                                        color="white"
                                    />
                                }
                                label={`Plano ${nomePlano || plano}`}
                                onClick={() => navigate(toTenant('/planos'))}
                                sx={{
                                    fontWeight: 700,
                                    display: { xs: 'none', sm: 'flex' },
                                    color: 'white',
                                    bgcolor: 'rgba(255,255,255,0.15)',
                                    border: '1px solid rgba(255,255,255,0.25)',
                                    fontSize: '0.75rem',
                                    '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' },
                                }}
                            />
                        </Tooltip>

                        <IconButton
                            aria-label="Abrir menu do usuário"
                            aria-controls="menu-appbar"
                            aria-haspopup="true"
                            onClick={(e) => setUserAnchor(e.currentTarget)}
                            sx={{ p: 0.5 }}
                        >
                            <Badge
                                overlap="circular"
                                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                                badgeContent={
                                    <Box
                                        sx={{
                                            width: 10,
                                            height: 10,
                                            borderRadius: '50%',
                                            bgcolor: 'success.light',
                                            border: '2px solid',
                                            borderColor: theme.palette.mode === 'dark' ? '#060A10' : 'primary.main',
                                        }}
                                    />
                                }
                            >
                                <Avatar
                                    sx={{
                                        bgcolor: avatar.color,
                                        color: avatar.textColor,
                                        width: 36,
                                        height: 36,
                                        fontSize: '0.875rem',
                                        fontWeight: 700,
                                        border: '2px solid rgba(255,255,255,0.3)',
                                    }}
                                >
                                    {avatar.initials}
                                </Avatar>
                            </Badge>
                        </IconButton>
                    </Stack>
                </Toolbar>
            </AppBar>

            <Box
                component="aside"
                sx={{
                    width: { md: larguraBarra },
                    flexShrink: { md: 0 },
                    transition: { md: transicaoBarra },
                }}
            >
                <Drawer
                    variant="temporary"
                    open={mobileOpen}
                    onClose={handleDrawerToggle}
                    ModalProps={{ keepMounted: true }}
                    sx={{
                        display: { xs: 'block', md: 'none' },
                        '& .MuiDrawer-paper': {
                            boxSizing: 'border-box',
                            width: DRAWER_WIDTH,
                            border: 'none',
                        },
                    }}
                >
                    {montarConteudo(false)}
                </Drawer>
                <Drawer
                    variant="permanent"
                    open
                    sx={{
                        display: { xs: 'none', md: 'block' },
                        '& .MuiDrawer-paper': {
                            boxSizing: 'border-box',
                            width: larguraBarra,
                            overflowX: 'hidden',
                            transition: transicaoBarra,
                            backgroundImage: `linear-gradient(to bottom, ${theme.palette.mode === 'dark' ? '#060A10' : theme.palette.primary.main} ${HEADER_HEIGHT}px, transparent ${HEADER_HEIGHT}px), linear-gradient(to right, ${theme.palette.divider}, ${theme.palette.divider})`,
                            backgroundSize: `100% 100%, 1px 100%`,
                            backgroundPosition: `0 0, 100% 0`,
                            backgroundRepeat: 'no-repeat',
                            border: 'none',
                        },
                    }}
                >
                    {montarConteudo(recolhida)}
                </Drawer>
            </Box>

            <Menu
                id="menu-appbar"
                anchorEl={userAnchor}
                open={Boolean(userAnchor)}
                onClose={() => setUserAnchor(null)}
                PaperProps={{
                    elevation: 3,
                    sx: { mt: 1, minWidth: 220, borderRadius: 2 },
                }}
            >
                <Box sx={{ px: 2, py: 1.5 }}>
                    <Typography variant="subtitle2" noWrap sx={{ fontWeight: 700 }}>
                        {username}
                    </Typography>
                    {roleChips.length > 0 ? (
                        <Stack
                            direction="row"
                            spacing={0.5}
                            useFlexGap
                            flexWrap="wrap"
                            sx={{ mt: 0.5 }}
                        >
                            {roleChips.map((rc) => (
                                <Chip
                                    key={rc.key}
                                    label={rc.label}
                                    size="small"
                                    color={rc.color}
                                    sx={{
                                        height: 20,
                                        fontSize: '0.65rem',
                                        fontWeight: 700,
                                        '& .MuiChip-label': { px: 0.75 },
                                    }}
                                />
                            ))}
                        </Stack>
                    ) : (
                        <Typography variant="caption" color="text.secondary">
                            Conta Conectada
                        </Typography>
                    )}
                </Box>
                <Divider aria-hidden="true" />
                {specialMenuItems.settings && (
                    <MenuItem
                        onClick={() => {
                            handleNavigate('/configuracoes');
                            setUserAnchor(null);
                        }}
                    >
                        <ListItemIcon aria-hidden="true">
                            <Lucide.Settings size={18} />
                        </ListItemIcon>
                        <ListItemText>Configurações</ListItemText>
                    </MenuItem>
                )}
                <MenuItem onClick={handleLogout} sx={{ color: 'error.main' }}>
                    <ListItemIcon aria-hidden="true">
                        <Lucide.LogOut size={18} color={theme.palette.error.main} />
                    </ListItemIcon>
                    <ListItemText>Sair da Conta</ListItemText>
                </MenuItem>
            </Menu>
        </>
    );
}
